import { type FC, useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { AccountRow } from "@/pages/postloss/modules/components/AccountRow";
import { AcceptDeclineActions } from "@/shared/components/EstateItems/AcceptDeclineActions";
import { CategoryEditDialog } from "@/shared/components/EstateItems/CategoryEditDialog";
import { SUPPORTED_CATEGORIES } from "@/shared/components/EstateItems/categoryEditHelpers";
import { EditEstateItemModal } from "@/shared/components/EstateItems/EditEstateItemModal";
import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import { buildItemSubtitle } from "@/pages/postloss/modules/components/itemSubtitles";

export const SecuredDebtsReview: FC<FlowCustomComponentProps> = ({ onComplete }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts, isLoading, updateItem, isUpdatingItem, deleteItem, isDeletingItem, itemActions } =
    useEstateItemActions(estateId);

  const securedDebts = filterByCategory(debts, "Secured debts");
  const [editingItem, setEditingItem] = useState<EstateItem | null>(null);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  const pendingItems = useMemo(
    () => securedDebts.filter((i) => i.extraction_status === "pending"),
    [securedDebts],
  );

  const handleAcceptAll = async () => {
    if (pendingItems.length === 0) return;
    setIsAcceptingAll(true);
    try {
      await Promise.allSettled(pendingItems.map((i) => itemActions.handleAccept(i.id)));
    } finally {
      setIsAcceptingAll(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      <div className="rounded-lg border border-primary bg-white p-6">
        <div className="flex flex-col gap-3">
          {securedDebts.length > 0 ? (
            securedDebts.map((item) => (
              <AccountRow
                key={item.id}
                item={item}
                subtitle={buildItemSubtitle(item)}
                onEdit={setEditingItem}
                actions={
                  item.extraction_status === "pending" ? (
                    <AcceptDeclineActions
                      onAccept={() => itemActions.handleAccept(item.id)}
                      onDecline={() => itemActions.handleDecline(item.id)}
                      loadingAction={itemActions.getItemLoadingAction(item.id)}
                    />
                  ) : undefined
                }
              />
            ))
          ) : (
            <p className="text-sm text-tertiary">No mortgages or loans added</p>
          )}
        </div>
      </div>

      <div className="mt-7 flex flex-col items-center">
        {pendingItems.length > 0 ? (
          <Button
            variant="brand"
            rounded
            onClick={handleAcceptAll}
            disabled={isAcceptingAll}
            className="w-full max-w-[400px]"
          >
            {isAcceptingAll ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Accept all
              </>
            ) : (
              <>
                Accept all <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        ) : (
          <Button
            variant="brand"
            rounded
            onClick={() => onComplete()}
            className="w-full max-w-[400px]"
          >
            Looks good <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {editingItem && SUPPORTED_CATEGORIES.has(editingItem.category) ? (
        <CategoryEditDialog
          key={editingItem.id}
          item={editingItem}
          isOpen
          onClose={() => setEditingItem(null)}
          updateItem={updateItem}
          isUpdatingItem={isUpdatingItem}
          deleteItem={deleteItem}
          isDeletingItem={isDeletingItem}
        />
      ) : editingItem ? (
        <EditEstateItemModal
          key={editingItem.id}
          isOpen
          onClose={() => setEditingItem(null)}
          estateId={editingItem.estate_id}
          item={editingItem}
        />
      ) : null}
    </div>
  );
};
